import { useRef, useId } from 'react';
import { Link } from 'react-router-dom';
import { useDropdownContext } from '../../context/DropdownContext';
import { PortalDropdownMenu } from './PortalDropdownMenu';

interface ActionDropdownItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface ActionDropdownProps {
  label: string;
  items: ActionDropdownItem[];
  variant?: 'default' | 'danger';
}

export function ActionDropdown({ label, items, variant = 'default' }: ActionDropdownProps) {
  const dropdownId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isOpen, openDropdown, closeDropdown } = useDropdownContext();

  const handleToggle = () => {
    if (isOpen(dropdownId)) {
      closeDropdown();
    } else {
      openDropdown(dropdownId);
    }
  };

  const buttonClasses = variant === 'danger'
    ? 'inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100'
    : 'inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50';

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={buttonClasses}
      >
        {label}
        <svg
          className={`ml-2 h-4 w-4 transition-transform ${isOpen(dropdownId) ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <PortalDropdownMenu
        dropdownId={dropdownId}
        triggerRef={buttonRef}
        align="right"
        width={160}
      >
        <div className="py-1">
          {items.map((item, index) => {
            if (item.href) {
              return (
                <Link
                  key={index}
                  to={item.href}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => closeDropdown()}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  item.onClick?.();
                  closeDropdown();
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </PortalDropdownMenu>
    </div>
  );
}
